// Saved compose drafts — shown at the top of the Drafts folder. These are the
// server-saved new-message drafts (shared, commentable); the thread rows below
// them are reply drafts.

import { FileText, Loader2, MessageSquare, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { useComposeDrafts, useDeleteComposeDraft } from '@/lib/compose-drafts';
import { useMe } from '@/lib/auth';

export function ComposeDraftsList({ onOpen }: { onOpen: (draftId: number) => void }) {
  const q = useComposeDrafts();
  const del = useDeleteComposeDraft();
  const me = useMe();
  const drafts = q.data?.drafts ?? [];

  if (q.isLoading) {
    return (
      <div className="flex items-center gap-2 border-b px-4 py-3 text-xs text-muted-foreground">
        <Loader2 className="size-3.5 animate-spin" /> Loading saved drafts…
      </div>
    );
  }
  if (drafts.length === 0) return null;

  return (
    <div className="border-b">
      <div className="bg-muted/40 px-4 py-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        New-message drafts
      </div>
      <ul>
        {drafts.map((d) => {
          const mine = d.authorUserId === me.data?.user.id;
          return (
            <li
              key={d.id}
              className="group flex cursor-pointer items-center gap-3 border-b px-4 py-2.5 last:border-b-0 hover:bg-muted/30"
              onClick={() => onOpen(d.id)}
            >
              <FileText className="size-4 shrink-0 text-muted-foreground" strokeWidth={1.75} />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium">
                  {d.subject || '(no subject)'}
                </div>
                <div className="truncate text-xs text-muted-foreground">
                  by {d.author}
                  {d.to.length > 0 && ` · to ${d.to[0]}${d.to.length > 1 ? ` +${d.to.length - 1}` : ''}`}
                  {' · '}
                  {new Date(d.updatedAt).toLocaleDateString(undefined, {
                    month: 'short',
                    day: 'numeric',
                  })}
                </div>
              </div>
              {d.commentCount > 0 && (
                <span className="inline-flex shrink-0 items-center gap-1 text-xs text-muted-foreground">
                  <MessageSquare className="size-3.5" strokeWidth={1.75} />
                  {d.commentCount}
                </span>
              )}
              {mine && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7 shrink-0 opacity-0 hover:text-destructive group-hover:opacity-100"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (confirm(`Delete draft "${d.subject || '(no subject)'}"?`)) {
                      del.mutate(d.id, { onSuccess: () => toast.success('Draft deleted') });
                    }
                  }}
                  aria-label="Delete draft"
                >
                  <Trash2 className="size-3.5" strokeWidth={1.75} />
                </Button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
